import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { ShopifyCart } from "@redotech/shopify-client/ajax";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { log } from "../log";
import { ExtensionShopifyClientContext } from "../shopify";

const isShopifyCartUrl = (url: string) =>
  /\/cart(\/|$)/i.test(url) &&
  !url.includes("api.getredo.com") &&
  !url.includes("localhost");

export function useCart(manualRefresh?: Symbol) {
  const [cart, setCart] = useState<ShopifyCart | undefined>(undefined);
  const shopifyClient = useRequiredContext(ExtensionShopifyClientContext);
  const [cartRecheckSymbol, recheckCart] = useState(Symbol());
  const cartRecheck = useDebounce(cartRecheckSymbol, 500);

  useEffect(() => {
    if (window.Tapcart?.registerEventHandler) {
      window.Tapcart?.registerEventHandler(
        "cart/updated",
        function (eventData) {
          recheckCart(Symbol());
        },
      );
    } else {
      function wrapFetch(fetch: typeof window.fetch) {
        return async function (this: any) {
          const url = arguments[0];
          try {
            return fetch.apply(this, arguments as any);
          } finally {
            if (isShopifyCartUrl(url)) {
              recheckCart(Symbol());
            }
          }
        };
      }

      function wrapXhrSend(send: typeof XMLHttpRequest.prototype.send) {
        return async function (this: XMLHttpRequest) {
          this.addEventListener("readystatechange", function (event) {
            if (this.readyState === XMLHttpRequest.HEADERS_RECEIVED) {
              if (isShopifyCartUrl(this.responseURL)) {
                recheckCart(Symbol());
              }
            }
          });
          return send.apply(this, arguments as any);
        };
      }

      const originalFetch = window.fetch;
      const originalSend = XMLHttpRequest.prototype.send;

      window.fetch = wrapFetch(fetch);

      XMLHttpRequest.prototype.send = wrapXhrSend(
        XMLHttpRequest.prototype.send,
      );

      return () => {
        window.fetch = originalFetch;
        XMLHttpRequest.prototype.send = originalSend;
      };
    }
  }, []);

  useLoad(
    async (signal) => {
      log("fetching cart");
      const newCart = await shopifyClient.cartGet(signal);
      if (!cart || !isEqual(cart, newCart)) {
        log("found new cart", { newCart });
        setCart(newCart);
      } else {
        log("cart unchanged");
      }
    },
    [cartRecheck, manualRefresh],
  );

  return cart;
}
